// Best-effort Jinja2 → BlockTree parser.
//
// Used only on legacy migration — for templates produced by our own serialiser,
// the block tree is canonical and we don't parse.
//
// What we recognise:
// - `{{ subject.sections['slug'] [or 'fallback'] }}` → SectionBlock (the `or` part
//   is stripped if present so we don't carry the fallback into the tree; the
//   serialiser re-emits it on save)
// - `{{ <path> [| filter(args)]* }}` → InlineVariable or standalone VariableBlock
// - Markdown text: `# Heading`, `---` divider, paragraphs (lines separated by
//   blank lines)
//
// Anything we don't recognise (`{% for %}`, `{% if %}`, `{% set %}`, custom
// expressions, ...) becomes a RawBlock AND sets `hasUnparseable=true`. The
// editor reads that flag to default the legacy template into Advanced mode
// rather than mangling structure into RawBlocks.

import { Block, BlockTree, FilterApplication, InlineNode, TEMPLATE_BLOCKS_VERSION } from '@/types';
import { newBlockId } from './block-tree';

export interface ParseResult {
  tree: BlockTree;
  // True if any block had to fall back to RawBlock. The UI uses this to
  // decide whether to default to Blocks mode or Advanced for a legacy template.
  hasUnparseable: boolean;
}

export function parseJinjaToBlocks(source: string): ParseResult {
  const tokens = tokenise(source);
  const state = { hasUnparseable: false };
  const blocks = parseTokens(tokens, state);
  return {
    tree: { version: TEMPLATE_BLOCKS_VERSION, blocks },
    hasUnparseable: state.hasUnparseable,
  };
}

// ---------- tokenisation ----------

type TokenKind = 'text' | 'expr' | 'tag' | 'comment';

interface Token {
  kind: TokenKind;
  // Inner content with any whitespace-control hyphens trimmed.
  content: string;
}

const DELIMITER_TABLE: Record<string, { close: string; kind: TokenKind }> = {
  '%': { close: '%}', kind: 'tag' },
  '{': { close: '}}', kind: 'expr' },
  '#': { close: '#}', kind: 'comment' },
};

function tokenise(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  let textStart = 0;

  const flushText = (end: number) => {
    if (end > textStart) {
      tokens.push({ kind: 'text', content: source.slice(textStart, end) });
    }
  };

  while (i < source.length) {
    const delim = source[i] === '{' ? DELIMITER_TABLE[source[i + 1]] : undefined;
    if (!delim) {
      i++;
      continue;
    }
    flushText(i);
    const closeIdx = source.indexOf(delim.close, i + 2);
    if (closeIdx === -1) {
      // Unclosed — treat the rest as text.
      textStart = i;
      break;
    }
    tokens.push({
      kind: delim.kind,
      content: trimWhitespaceControl(source.slice(i + 2, closeIdx)),
    });
    i = closeIdx + 2;
    textStart = i;
  }
  flushText(source.length);
  return tokens;
}

function trimWhitespaceControl(inner: string): string {
  let result = inner;
  if (result.startsWith('-')) {
    result = result.slice(1);
  }
  if (result.endsWith('-')) {
    result = result.slice(0, -1);
  }
  return result.trim();
}

// ---------- parsing ----------

interface ParseState {
  hasUnparseable: boolean;
}

function parseTokens(tokens: Token[], state: ParseState): Block[] {
  const blocks: Block[] = [];
  let i = 0;

  while (i < tokens.length) {
    const tok = tokens[i];

    if (tok.kind === 'comment') {
      // Comments are stripped — they don't survive the round trip.
      i++;
      continue;
    }

    if (tok.kind === 'tag') {
      // We don't handle ANY tags in v1. Everything goes to a Raw block and
      // flags the template as needing Advanced mode. We greedily slurp until
      // the matching close (`endfor`/`endif`/etc.) so the raw text stays
      // structurally complete.
      const raw = consumeRawTag(tokens, i);
      state.hasUnparseable = true;
      blocks.push({ id: newBlockId(), kind: 'raw', source: raw.source });
      i = raw.end;
      continue;
    }

    // text or expr — collect a run of consecutive text/expr tokens, then split
    // it into block-level pieces (headings, dividers, paragraphs with inline
    // variables).
    const runStart = i;
    while (i < tokens.length && (tokens[i].kind === 'text' || tokens[i].kind === 'expr')) {
      i++;
    }
    const runTokens = tokens.slice(runStart, i);
    blocks.push(...parseTextRun(runTokens));
  }

  return blocks;
}

// Greedy slurp from a tag token to its matching close, returning the source
// text and the index of the token after the close. If the opener has no
// matching close, returns just the opener as raw.
function consumeRawTag(tokens: Token[], start: number): { source: string; end: number } {
  const opener = tokens[start];
  const openKeyword = opener.content.split(/\s+/)[0];
  const closeKeyword = CLOSE_KEYWORDS[openKeyword];

  if (!closeKeyword) {
    return { source: `{% ${opener.content} %}`, end: start + 1 };
  }

  const parts: string[] = [`{% ${opener.content} %}`];
  let depth = 1;
  for (let i = start + 1; i < tokens.length; i++) {
    const t = tokens[i];
    parts.push(renderToken(t));
    if (t.kind !== 'tag') {
      continue;
    }
    const k = t.content.split(/\s+/)[0];
    if (k === openKeyword) {
      depth++;
    } else if (k === closeKeyword) {
      depth--;
      if (depth === 0) {
        return { source: parts.join(''), end: i + 1 };
      }
    }
  }
  // Unmatched — return what we have, parser will flag unparseable.
  return { source: parts.join(''), end: tokens.length };
}

function renderToken(t: Token): string {
  if (t.kind === 'tag') {
    return `{% ${t.content} %}`;
  }
  if (t.kind === 'expr') {
    return `{{ ${t.content} }}`;
  }
  if (t.kind === 'comment') {
    return `{# ${t.content} #}`;
  }
  return t.content;
}

const CLOSE_KEYWORDS: Record<string, string> = {
  for: 'endfor',
  if: 'endif',
  block: 'endblock',
  macro: 'endmacro',
  call: 'endcall',
  filter: 'endfilter',
  with: 'endwith',
  autoescape: 'endautoescape',
  raw: 'endraw',
};

// ---------- expression parsing ----------

// Recognise `subject.sections['slug']` or `subject.sections["slug"]`, optionally
// followed by `or '<fallback>'`. The `or` part is discarded — the serialiser
// re-emits a canonical fallback on save.
const SECTION_RE = /^subject\.sections\[(['"])(.+?)\1\](?:\s+or\s+(['"]).*\3)?$/;

function parseExpression(content: string): { path: string; filters: FilterApplication[] } | null {
  const trimmed = content.trim();
  // Split on top-level `|` — naive but adequate. We don't currently support
  // nested filter expressions with pipes inside arguments.
  const parts = splitFilterPipeline(trimmed);
  if (parts.length === 0) {
    return null;
  }
  const path = parts[0].trim();
  const filters = parts
    .slice(1)
    .map(parseFilterCall)
    .filter((f): f is FilterApplication => !!f);
  return { path, filters };
}

function splitFilterPipeline(s: string): string[] {
  return splitTopLevel(s, '|', { keepEmptyTrailing: true });
}

function parseFilterCall(part: string): FilterApplication | null {
  const trimmed = part.trim();
  // `name` or `name(arg, arg)`
  const m = /^([a-zA-Z_]\w*)(?:\((.*)\))?$/.exec(trimmed);
  if (!m) {
    return null;
  }
  const name = m[1];
  const argsStr = m[2];
  if (!argsStr) {
    return { name, args: [] };
  }
  const args = splitTopLevel(argsStr, ',', { keepEmptyTrailing: false }).map(unquoteArg);
  return { name, args };
}

// Split `s` on `delimiter` at depth zero, respecting paren/bracket nesting and
// string literals. The two splitting modes:
//   - keepEmptyTrailing=true:  always push the final buffer (used for pipelines
//                              where "x |" should yield ["x", ""]).
//   - keepEmptyTrailing=false: only push trailing buffer if non-empty (used for
//                              argument lists where trailing whitespace is meaningless).
function splitTopLevel(
  s: string,
  delimiter: string,
  opts: { keepEmptyTrailing: boolean }
): string[] {
  const parts: string[] = [];
  let depth = 0;
  let inString: string | null = null;
  let buf = '';
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (inString) {
      buf += ch;
      if (ch === inString && s[i - 1] !== '\\') {
        inString = null;
      }
      continue;
    }
    if (ch === '"' || ch === "'") {
      inString = ch;
      buf += ch;
      continue;
    }
    if (ch === '(' || ch === '[') {
      depth++;
    } else if (ch === ')' || ch === ']') {
      depth--;
    }

    if (ch === delimiter && depth === 0) {
      parts.push(buf);
      buf = '';
      continue;
    }
    buf += ch;
  }
  if (opts.keepEmptyTrailing || buf.trim()) {
    parts.push(buf);
  }
  return parts;
}

function unquoteArg(arg: string): string {
  const trimmed = arg.trim();
  const m = /^(['"])(.*)\1$/.exec(trimmed);
  if (m) {
    return m[2].replaceAll(String.raw`\'`, "'").replaceAll(String.raw`\"`, '"');
  }
  // Non-string-literal arg — store with the "=" prefix so the serialiser
  // emits it unquoted.
  return `=${trimmed}`;
}

// ---------- text / expr run parsing ----------

// Convert a run of consecutive text + expr tokens into block-level pieces.
// Heuristic:
//   1. Split the run's full source by blank lines into paragraph candidates.
//   2. For each candidate, if it's exactly `---`, emit a DividerBlock.
//   3. If it starts with `#` (followed by optional more `#`s and a space),
//      emit a HeadingBlock.
//   4. Otherwise emit a ParagraphBlock with the candidate's inline nodes.
// Inline expressions inside a paragraph become InlineVariable nodes; an
// expression on its own line (no text around it) becomes a SectionBlock if it
// looks like a section ref, or a standalone VariableBlock if it doesn't.
type Segment = { type: 'text'; value: string } | { type: 'var'; node: InlineNode };

function tokensToSegments(tokens: Token[]): Segment[] {
  const segments: Segment[] = [];
  for (const t of tokens) {
    if (t.kind === 'text') {
      segments.push({ type: 'text', value: t.content });
      continue;
    }
    if (t.kind !== 'expr') {
      continue;
    }
    const parsed = parseExpression(t.content);
    if (parsed) {
      segments.push({
        type: 'var',
        node: { type: 'var', path: parsed.path, filters: parsed.filters },
      });
    } else {
      // Couldn't parse the expression — keep it verbatim as text.
      segments.push({ type: 'text', value: `{{ ${t.content} }}` });
    }
  }
  return segments;
}

// Split a list of segments into paragraph candidates by blank lines that
// appear inside text segments. Whitespace-only edge segments are dropped from
// each candidate.
function segmentsToParagraphs(segments: Segment[]): Segment[][] {
  const paragraphs: Segment[][] = [];
  let current: Segment[] = [];
  const pushCurrent = () => {
    while (current.length > 0 && current[0].type === 'text' && !current[0].value.trim()) {
      current.shift();
    }
    while (current.length > 0) {
      const tail = current.at(-1)!;
      if (tail.type !== 'text' || tail.value.trim()) {
        break;
      }
      current.pop();
    }
    if (current.length > 0) {
      paragraphs.push(current);
    }
    current = [];
  };
  for (const seg of segments) {
    if (seg.type !== 'text') {
      current.push(seg);
      continue;
    }
    const parts = seg.value.split(/\n\s*\n/);
    for (let i = 0; i < parts.length; i++) {
      if (parts[i]) {
        current.push({ type: 'text', value: parts[i] });
      }
      if (i < parts.length - 1) {
        pushCurrent();
      }
    }
  }
  pushCurrent();
  return paragraphs;
}

// Strip leading/trailing newlines from the edge text segments of each
// paragraph so the line breaks that delimit it don't bleed into rendered text.
function trimParagraphEdges(paragraphs: Segment[][]) {
  for (const para of paragraphs) {
    const first = para[0];
    if (first?.type === 'text') {
      para[0] = { type: 'text', value: first.value.replace(/^\n+/, '') };
    }
    const last = para.at(-1);
    if (last?.type === 'text') {
      para[para.length - 1] = { type: 'text', value: last.value.replace(/\n+$/, '') };
    }
  }
}

function parseTextRun(tokens: Token[]): Block[] {
  const segments = tokensToSegments(tokens);
  const paragraphs = segmentsToParagraphs(segments);
  trimParagraphEdges(paragraphs);
  return paragraphs.flatMap((p) => paragraphToBlocks(p));
}

function paragraphToBlocks(segments: Segment[]): Block[] {
  const single = trySingleSegmentBlock(segments);
  if (single) {
    return single;
  }

  const headingWithVars = tryHeadingWithInlineVars(segments);
  if (headingWithVars) {
    return headingWithVars;
  }

  return [buildParagraphBlock(segments)];
}

// Single-segment paragraphs are either: a divider, a heading, a section ref,
// or a standalone variable. Returns null when nothing matches.
function trySingleSegmentBlock(segments: Segment[]): Block[] | null {
  if (segments.length !== 1) {
    return null;
  }
  const only = segments[0];

  if (only.type === 'var') {
    return [varSegmentToBlock(only.node)];
  }

  const text = only.value.trim();
  if (text === '---') {
    return [{ id: newBlockId(), kind: 'divider' }];
  }
  const heading = /^(#{1,6})\s+(.*)$/.exec(text);
  if (heading) {
    return [
      {
        id: newBlockId(),
        kind: 'heading',
        level: heading[1].length as 1 | 2 | 3 | 4 | 5 | 6,
        content: [{ type: 'text', value: heading[2] }],
      },
    ];
  }
  return null;
}

function varSegmentToBlock(node: InlineNode): Block {
  if (node.type !== 'var') {
    return { id: newBlockId(), kind: 'paragraph', content: [node] };
  }
  // The expression's `or 'fallback'` suffix sits inside `path` as a single
  // string because our pipeline-splitter sees no top-level pipe. We test for a
  // section ref pattern that allows the optional fallback.
  const m = SECTION_RE.exec(node.path);
  if (m) {
    return { id: newBlockId(), kind: 'section', slug: m[2] };
  }
  return {
    id: newBlockId(),
    kind: 'variable',
    path: node.path,
    filters: node.filters,
  };
}

function tryHeadingWithInlineVars(segments: Segment[]): Block[] | null {
  const first = segments[0];
  if (segments.length < 2 || first?.type !== 'text') {
    return null;
  }
  const m = /^(#{1,6})\s+/.exec(first.value);
  if (!m) {
    return null;
  }
  const level = m[1].length as 1 | 2 | 3 | 4 | 5 | 6;
  const remainder = first.value.slice(m[0].length);
  const content: InlineNode[] = remainder ? [{ type: 'text', value: remainder }] : [];
  for (const seg of segments.slice(1)) {
    content.push(seg.type === 'text' ? { type: 'text', value: seg.value } : seg.node);
  }
  return [{ id: newBlockId(), kind: 'heading', level, content }];
}

function buildParagraphBlock(segments: Segment[]): Block {
  const content: InlineNode[] = segments.map((s) =>
    s.type === 'text' ? { type: 'text' as const, value: s.value } : s.node
  );
  return { id: newBlockId(), kind: 'paragraph', content };
}
